import {
  authenticatedRequest,
  type AuthenticatedTelegramUser,
} from "./telegramAuth";
import {
  emptyDraft,
  type CatalogLocationFilter,
  type ChatAttachment,
  type SellerProfile,
  type SellerReview,
  type SellerReviewManage,
  type SellerReviewsPage,
  type ReviewOpportunity,
  type ModerationReview,
  type ChatInbox,  type AdComment,
  type ChatInboxItem,
  type ChatMessage,
  type CityLocation,
  type Conversation,
  type Draft,
  type Listing,
  type RegionLocation,
} from "./types";

interface ServerCategory {
  name: string;
  slug: string;
}

interface ServerRegion {
  code: string;
  name: string;
  type?: string;
  federal_district?: string;
}

interface ServerCity {
  id: string;
  name: string;
  region: Pick<ServerRegion, "code" | "name">;
  is_region_capital: boolean;
}

interface RegionCollectionResponse {
  items: ServerRegion[];
}

interface CityCollectionResponse {
  items: ServerCity[];
}

interface ServerAd {
  id: string;
  title: string;
  description: string;
  amount_minor: number | null;
  currency: string;
  price_type: string;
  city: string;
  city_id?: string | null;
  region?: Pick<ServerRegion, "code" | "name"> | null;
  condition: string;
  lifecycle: string;
  published_at: string | null;
  category: ServerCategory;
  seller: { id: string; display_name: string; city: string };
  seller_rating: number | null;
  product_rating?: number | null;
  product_reviews_count?: number;
  comments_enabled?: boolean;
  comments_count?: number;
  media: Array<{ url: string; position: number; is_cover: boolean }>;
  favorite: boolean;
}

interface AdCollectionResponse {
  items: ServerAd[];
  next_cursor: string | null;
}

interface FavoritesResponse {
  items: ServerAd[];
}

interface ServerApiError {
  error?: { code?: string; detail?: unknown };
}

export class MarketplaceRequestError extends Error {
  readonly code: string;
  readonly retryAfterSeconds: number | null;

  constructor(code: string, retryAfterSeconds: number | null = null) {
    super(code);
    this.name = "MarketplaceRequestError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function retryAfterFromError(body: ServerApiError | undefined): number | null {
  const detail = body?.error?.detail;
  if (!detail || typeof detail !== "object" || !("retry_after" in detail)) return null;
  const retryAfter = Number((detail as { retry_after?: unknown }).retry_after);
  return Number.isFinite(retryAfter) && retryAfter >= 1 ? Math.ceil(retryAfter) : null;
}

interface CreatedAdResponse {
  ad: { id: string };
}

interface UploadSessionResponse {
  session: {
    id: string;
    state: "created" | "uploaded" | "committed" | "expired" | "failed";
    upload_url: string;
    commit_url: string;
  };
}

interface CommittedUploadResponse {
  asset: { id: string };
}

interface PublishedAdResponse {
  ad: ServerAd;
}

interface ServerAttachment {
  id: string;
  content_type: string;
  size_bytes: number;
  width: number;
  height: number;
  url: string;
}

interface ServerMessage {
  id: string;
  author: "me" | "other";
  text: string;
  sequence: number;
  created_at: string;
  sent_at?: string;
  status?: "sent" | "read";
  attachments: ServerAttachment[];
}

interface ServerConversation {
  id: string;
  listing: { id: string; title: string };
  counterpart: { display_name: string; city: string };
  last_message_at: string | null;
  unread_count: number;
  blocked_by_me?: boolean;
  deal_status?: "open" | "sold" | "closed";
  can_mark_sold?: boolean;
  can_rate?: boolean;
  is_support?: boolean;
  created_at: string;
}

interface ConversationCollectionResponse {
  items: ServerConversation[];
}

interface ServerInboxItem {
  id: number;
  kind: "message";
  conversation_id: string;
  listing_title: string;
  message_sequence: number;
  created_at: string;
  read_at: string | null;
}

interface InboxResponse {
  items: ServerInboxItem[];
  unread_count: number;
}

interface ConversationResponse {
  conversation: ServerConversation;
}

interface MessageCollectionResponse {
  items: ServerMessage[];
}

interface MessageResponse {
  message: ServerMessage;
}

export type ConversationEvent =
  | { type: "message.created"; message: ChatMessage }
  | { type: "conversation.read"; author: "me" | "other"; lastReadSequence: number };

export type ConversationEventListener = (event: ConversationEvent) => void;

export interface MarketplaceApi {
  readonly offline: boolean;
  readonly submitted: boolean;
  setOffline(value: boolean): void;
  getListings(category?: string, location?: CatalogLocationFilter): Promise<Listing[]>;
  getRegions(): Promise<RegionLocation[]>;
  searchCities(regionCode: string, query: string): Promise<CityLocation[]>;
  toggleFavorite(id: string): Promise<boolean>;
  getFavorites(): Promise<Listing[]>;
  getSellerProfile(sellerId: string): Promise<SellerProfile>;
  getSellerReviews(sellerId: string, rating?: number, cursor?: string): Promise<SellerReviewsPage>;
  getReviewOpportunities(sellerId: string, cursor?: string): Promise<{ items: ReviewOpportunity[]; nextCursor: string | null; hasMore: boolean }>;
  getSellerReviewManage(reviewId: string): Promise<SellerReviewManage>;
  getModerationQueue(): Promise<ModerationReview[]>;
  moderateSellerReview(reviewId: string, action: "approve" | "reject" | "hide"): Promise<void>;
  createSellerReview(sellerId: string, input: { adId: string; conversationId: string; rating: number; text: string; outcome: string }): Promise<{ id: string; status: string }>;
  updateSellerReview(reviewId: string, input: { version: number; rating: number; text: string; outcome: string }): Promise<{ version: number; status: string }>;
  hideSellerReview(reviewId: string): Promise<void>;
  replySellerReview(reviewId: string, text: string): Promise<void>;
  reportSellerReview(reviewId: string, reason: string, details?: string): Promise<void>;
  getAdComments(adId: string): Promise<AdComment[]>;
  addAdComment(adId: string, text: string, parentId?: string): Promise<AdComment>;
  getConversations(): Promise<Conversation[]>;
  getInbox(): Promise<ChatInbox>;
  setTelegramWriteAccess(granted: boolean): Promise<boolean>;
  blockConversation(chatId: string): Promise<boolean>;
  unblockConversation(chatId: string): Promise<boolean>;
  markConversationSold(chatId: string): Promise<Conversation>;
  rateAd(adId: string, conversationId: string, rating: number, text?: string): Promise<void>;
  reportMessage(chatId: string, messageId: string, reason: "spam" | "fraud" | "abuse" | "other"): Promise<void>;
  startConversation(listingId: string): Promise<Conversation>;
  getConversationMessages(chatId: string, afterSequence?: number): Promise<ChatMessage[]>;
  markConversationRead(chatId: string, lastReadSeq: number): Promise<number>;
  subscribeToConversation(
    chatId: string,
    listener: ConversationEventListener,
    onReconnect?: (afterSequence: number) => void,
    initialSequence?: number,
  ): () => void;
  sendMessage(chatId: string, text: string, files?: File[], clientMessageId?: string): Promise<ChatMessage>;
  releaseConversationMedia(chatId: string): void;
  getDraft(): Draft;
  saveDraft(patch: Partial<Draft>): Promise<Draft>;
  submitDraft(draft: Draft, files: File[]): Promise<Listing | void>;
}

function formatFeedDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function conditionLabel(value: string): string {
  return {
    new: "Новое",
    used: "Б/у",
    for_parts: "На запчасти",
    not_applicable: "Не применимо",
  }[value] ?? value;
}

function coverUrl(media: ServerAd["media"]): string | null {
  const ordered = [...media].sort((left, right) => {
    if (left.is_cover !== right.is_cover) return left.is_cover ? -1 : 1;
    return left.position - right.position;
  });
  return ordered[0]?.url ?? null;
}

function mapRegion(region: ServerRegion): RegionLocation {
  return {
    code: region.code,
    name: region.name,
    type: region.type ?? "",
    federalDistrict: region.federal_district ?? "",
  };
}

function mapCity(city: ServerCity): CityLocation {
  return {
    id: city.id,
    name: city.name,
    region: { code: city.region.code, name: city.region.name },
    isRegionCapital: city.is_region_capital === true,
  };
}

function mapAd(ad: ServerAd): Listing {
  return {
    id: ad.id,
    title: ad.title,
    price: ad.amount_minor === null ? 0 : ad.amount_minor / 100,
    city: ad.city || ad.seller.city,
    cityId: ad.city_id ?? null,
    regionCode: ad.region?.code ?? null,
    age: formatFeedDate(ad.published_at),
    condition: conditionLabel(ad.condition),
    category: ad.category.name,
    emoji: "•",
    tint: "lavender",
    favorite: ad.favorite,
    status: ad.lifecycle === "sold" ? "sold" : "active",
    seller: ad.seller.display_name,
    sellerId: ad.seller.id,
    sellerRating: ad.seller_rating,
    productRating: ad.product_rating ?? null,
    productReviewsCount: ad.product_reviews_count ?? 0,
    commentsEnabled: ad.comments_enabled ?? true,
    commentsCount: ad.comments_count ?? 0,
    product: ad.category.name,
    description: ad.description,
    imageUrl: coverUrl(ad.media),
  };
}

function mapSellerReview(item: any): SellerReview {
  return {
    id: item.id,
    author: { displayName: item.author?.display_name ?? "Пользователь", avatarUrl: item.author?.avatar_url ?? null },
    rating: item.rating,
    text: item.text,
    outcome: item.outcome,
    createdAt: item.created_at,
    updatedAt: item.updated_at ?? null,
    edited: item.edited === true,
    contributesToRating: item.contributes_to_rating !== false,
    ad: item.ad,
    reply: item.reply ? { text: item.reply.text, createdAt: item.reply.created_at, updatedAt: item.reply.updated_at ?? null, edited: item.reply.edited === true } : null,
  };
}

function mapSellerProfile(body: any): SellerProfile {
  return {
    seller: { id: String(body.seller.id), displayName: body.seller.display_name, avatarUrl: body.seller.avatar_url ?? null, city: body.seller.city ?? null, memberSince: body.seller.member_since },
    counts: { activeAds: body.counts.active_ads, reviews: body.counts.reviews },
    reputation: { average: body.reputation.average ?? null, reviewersCount: body.reputation.reviewers_count, reviewsCount: body.reputation.reviews_count, distribution: body.reputation.distribution ?? {} },
    reviews: (body.reviews ?? []).map(mapSellerReview),
  };
}

async function parse<T>(response: Response): Promise<T | undefined> {
  try {
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

async function request<T>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  const response = await fetch(input, authenticatedRequest(init));
  const body = await parse<T & ServerApiError>(response);
  if (!response.ok) {
    const errorBody = body as ServerApiError | undefined;
    const code = errorBody?.error?.code ?? "marketplace_request_failed";
    throw new MarketplaceRequestError(code, retryAfterFromError(errorBody));
  }
  return body as T;
}

function categorySlug(category: string): string {
  return {
    Дом: "dom",
    Техника: "tekhnika",
    Одежда: "odezhda",
    Хобби: "khobbi",
    Детское: "detskoe",
    Услуги: "uslugi",
    "Электронные товары": "elektronnye-tovary",
  }[category] ?? category;
}

function conditionCode(condition: string): string {
  return {
    Новое: "new",
    "Отличное состояние": "used",
    "Хорошее состояние": "used",
    "Есть следы использования": "used",
  }[condition] ?? condition;
}

function idempotencyKey(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  return generated ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function listingPayload(draft: Draft): Record<string, unknown> {
  const amount = Number(draft.price);
  if (!draft.title.trim() || !draft.description.trim() || !draft.category || !draft.cityId) {
    throw new Error("draft_invalid");
  }
  if (!Number.isFinite(amount) || amount < 0) throw new Error("draft_invalid");
  const priceType = amount > 0 ? "fixed" : "free";
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    category: categorySlug(draft.category),
    city_id: draft.cityId,
    condition: conditionCode(draft.condition),
    price_type: priceType,
    amount_minor: priceType === "fixed" ? Math.round(amount * 100) : 0,
    comments_enabled: draft.commentsEnabled ?? true,
  };
}

function mapAttachment(attachment: ServerAttachment): ChatAttachment {
  return {
    id: attachment.id,
    contentType: attachment.content_type,
    sizeBytes: attachment.size_bytes,
    width: attachment.width,
    height: attachment.height,
    url: attachment.url,
  };
}

function mapMessage(message: ServerMessage): ChatMessage {
  return {
    id: message.id,
    author: message.author,
    text: message.text,
    sequence: message.sequence,
    status: message.status ?? "sent",
    time: formatFeedDate(message.created_at),
    attachments: message.attachments.map(mapAttachment),
  };
}

function mapConversation(conversation: ServerConversation): Conversation {
  return {
    id: conversation.id,
    name: conversation.counterpart.display_name || "Пользователь",
    listing: conversation.listing.title,
    listingId: conversation.listing.id,
    preview: conversation.unread_count ? "Новое сообщение" : "Диалог начат",
    time: formatFeedDate(conversation.last_message_at ?? conversation.created_at),
    unread: conversation.unread_count,
    blockedByMe: conversation.blocked_by_me === true,
    dealStatus: conversation.deal_status ?? "open",
    canMarkSold: conversation.can_mark_sold === true,
    canRate: conversation.can_rate === true,
    isSupport: conversation.is_support === true,
    messages: [],
  };
}

function isSupportChat(chatId: string): boolean {
  return chatId === "support" || chatId.startsWith("support:");
}

export function newClientMessageId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new Error("client_message_id_unavailable");
  return generated;
}

function mapInboxItem(item: ServerInboxItem): ChatInboxItem {
  return {
    id: item.id,
    kind: item.kind,
    conversationId: item.conversation_id,
    listingTitle: item.listing_title,
    messageSequence: item.message_sequence,
    createdAt: item.created_at,
    readAt: item.read_at,
  };
}

export class ServerApi implements MarketplaceApi {
  private offlineState = false;
  private draft = emptyDraft();
  private submittedState = false;
  private favoriteIds = new Set<string>();
  private privateAttachmentUrls = new Map<string, Map<string, string>>();

  get offline(): boolean {
    return this.offlineState;
  }

  get submitted(): boolean {
    return this.submittedState;
  }

  setOffline(value: boolean): void {
    this.offlineState = value;
  }

  private async commitUpload(file: File, uploadKey = idempotencyKey()): Promise<string> {
    if (!file.name || !file.type || !file.size) throw new Error("media_invalid");
    const upload = await request<UploadSessionResponse>("/api/v1/media/upload-sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        idempotency_key: uploadKey,
      }),
    });
    if (upload.session.state === "created") {
      const form = new FormData();
      form.append("file", file, file.name);
      await request<unknown>(upload.session.upload_url, { method: "POST", body: form });
    } else if (upload.session.state !== "uploaded" && upload.session.state !== "committed") {
      throw new Error("media_upload_unavailable");
    }
    const committed = await request<CommittedUploadResponse>(upload.session.commit_url, { method: "POST" });
    return committed.asset.id;
  }

  private async materializePrivateAttachment(
    chatId: string,
    attachment: ServerAttachment,
  ): Promise<ChatAttachment> {
    const mapped = mapAttachment(attachment);
    const known = this.privateAttachmentUrls.get(chatId)?.get(attachment.id);
    if (known) return { ...mapped, url: known };
    try {
      const response = await fetch(attachment.url, authenticatedRequest());
      if (!response.ok) return { ...mapped, url: "" };
      const blob = await response.blob();
      if (!blob.size || !blob.type.startsWith("image/")) return { ...mapped, url: "" };
      const objectUrl = URL.createObjectURL(blob);
      let chatUrls = this.privateAttachmentUrls.get(chatId);
      if (!chatUrls) {
        chatUrls = new Map();
        this.privateAttachmentUrls.set(chatId, chatUrls);
      }
      chatUrls.set(attachment.id, objectUrl);
      return { ...mapped, url: objectUrl };
    } catch {
      return { ...mapped, url: "" };
    }
  }

  private async materializeMessage(chatId: string, message: ServerMessage): Promise<ChatMessage> {
    const attachments = await Promise.all(
      message.attachments.map((attachment) => this.materializePrivateAttachment(chatId, attachment)),
    );
    return { ...mapMessage(message), attachments };
  }

  private async uploadChatAttachments(files: File[], clientMessageId: string): Promise<string[]> {
    if (files.length > 4) throw new Error("attachment_invalid");
    const assetIds: string[] = [];
    for (let position = 0; position < files.length; position += 1) {
      const file = files[position];
      assetIds.push(await this.commitUpload(file, `chat:${clientMessageId}:${position}`));
    }
    return assetIds;
  }

  releaseConversationMedia(chatId: string): void {
    const chatUrls = this.privateAttachmentUrls.get(chatId);
    if (!chatUrls) return;
    Array.from(chatUrls.values()).forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    this.privateAttachmentUrls.delete(chatId);
  }

  async getListings(category = "Всё", location: CatalogLocationFilter = {}): Promise<Listing[]> {
    const params = new URLSearchParams();
    if (category !== "Всё") params.set("category", categorySlug(category));
    if (location.regionCode) params.set("region", location.regionCode);
    if (location.cityId) params.set("city_id", location.cityId);
    const query = params.size ? `?${params.toString()}` : "";
    const body = await request<AdCollectionResponse>(`/api/v1/ads${query}`);
    for (const item of body.items) {
      if (item.favorite) this.favoriteIds.add(item.id);
      else this.favoriteIds.delete(item.id);
    }
    return body.items.map(mapAd);
  }

  async getRegions(): Promise<RegionLocation[]> {
    const body = await request<RegionCollectionResponse>("/api/v1/geography/regions");
    return body.items.map(mapRegion);
  }

  async searchCities(regionCode: string, query: string): Promise<CityLocation[]> {
    const normalizedRegion = regionCode.trim();
    const normalizedQuery = query.trim();
    if (!normalizedRegion || normalizedQuery.length < 2) return [];
    const params = new URLSearchParams({ region: normalizedRegion, q: normalizedQuery });
    const body = await request<CityCollectionResponse>(`/api/v1/geography/cities?${params.toString()}`);
    return body.items.map(mapCity);
  }

  async toggleFavorite(id: string): Promise<boolean> {
    const nextFavorite = !this.favoriteIds.has(id);
    const current = await request<{ favorite: boolean }>(
      `/api/v1/ads/${encodeURIComponent(id)}/favorite`,
      { method: nextFavorite ? "PUT" : "DELETE" },
    );
    if (current.favorite) this.favoriteIds.add(id);
    else this.favoriteIds.delete(id);
    return current.favorite;
  }

  async getFavorites(): Promise<Listing[]> {
    const body = await request<FavoritesResponse>("/api/v1/favorites");
    for (const item of body.items) this.favoriteIds.add(item.id);
    return body.items.map(mapAd);
  }

  async getSellerProfile(sellerId: string): Promise<SellerProfile> {
    const body = await request<any>(`/api/v1/sellers/${encodeURIComponent(sellerId)}`);
    return mapSellerProfile(body);
  }

  async getSellerReviews(sellerId: string, rating?: number, cursor?: string): Promise<SellerReviewsPage> {
    const params = new URLSearchParams();
    if (rating) params.set("rating", String(rating));
    if (cursor) params.set("cursor", cursor);
    const query = params.size ? `?${params.toString()}` : "";
    const body = await request<{ items: any[]; next_cursor: string | null; has_more: boolean }>(`/api/v1/sellers/${encodeURIComponent(sellerId)}/reviews${query}`);
    return { items: body.items.map(mapSellerReview), nextCursor: body.next_cursor, hasMore: body.has_more };
  }

  async getReviewOpportunities(sellerId: string, cursor?: string): Promise<{ items: ReviewOpportunity[]; nextCursor: string | null; hasMore: boolean }> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    const body = await request<{ items: any[]; next_cursor: string | null; has_more: boolean }>(`/api/v1/sellers/${encodeURIComponent(sellerId)}/review-opportunities${query}`);
    return {
      items: body.items.map((item) => ({
        adId: item.ad_id,
        title: item.title,
        conversationId: item.conversation_id,
        seller: { id: item.seller.id, displayName: item.seller.display_name },
        existingReviewId: item.existing_review_id,
        canCreate: item.can_create,
        reasonCode: item.reason_code,
        eligibilityExpiresAt: item.eligibility_expires_at,
        confirmationType: item.confirmation_type,
      })),
      nextCursor: body.next_cursor,
      hasMore: body.has_more,
    };
  }

  async getSellerReviewManage(reviewId: string): Promise<SellerReviewManage> {
    const body = await request<any>(`/api/v1/reviews/${encodeURIComponent(reviewId)}`);
    return { reviewId: body.review_id, version: body.version, status: body.status, editableUntil: body.editable_until, actions: body.actions };
  }

  async getModerationQueue(): Promise<ModerationReview[]> {
    const body = await request<{ items: any[] }>("/api/v1/reviews/moderation-queue");
    return body.items.map((item) => ({
      reviewId: item.review_id,
      status: item.status,
      rating: item.rating,
      text: item.text,
      author: item.author,
      seller: item.seller,
      ad: item.ad,
      reports: item.reports,
    }));
  }

  async moderateSellerReview(reviewId: string, action: "approve" | "reject" | "hide"): Promise<void> {
    await request(`/api/v1/reviews/${encodeURIComponent(reviewId)}/moderate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    });
  }

  async createSellerReview(sellerId: string, input: { adId: string; conversationId: string; rating: number; text: string; outcome: string }): Promise<{ id: string; status: string }> {
    const body = await request<{ review_id: string; status: string }>(`/api/v1/sellers/${encodeURIComponent(sellerId)}/reviews`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ad_id: input.adId, conversation_id: input.conversationId, rating: input.rating, text: input.text, outcome: input.outcome }),
    });
    return { id: body.review_id, status: body.status };
  }

  async updateSellerReview(reviewId: string, input: { version: number; rating: number; text: string; outcome: string }): Promise<{ version: number; status: string }> {
    const body = await request<{ version: number; status: string }>(`/api/v1/reviews/${encodeURIComponent(reviewId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    return body;
  }

  async hideSellerReview(reviewId: string): Promise<void> {
    await request(`/api/v1/reviews/${encodeURIComponent(reviewId)}`, { method: "DELETE" });
  }

  async replySellerReview(reviewId: string, text: string): Promise<void> {
    await request(`/api/v1/reviews/${encodeURIComponent(reviewId)}/reply`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
  }

  async reportSellerReview(reviewId: string, reason: string, details = ""): Promise<void> {
    await request(`/api/v1/reviews/${encodeURIComponent(reviewId)}/report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason, details }),
    });
  }

  async getAdComments(adId: string): Promise<AdComment[]> {
    const body = await request<{ items: Array<{ id: string; author: string; text: string; parent_id?: string | null; created_at: string }> }>(
      `/api/v1/ads/${encodeURIComponent(adId)}/comments`,
    );
    return body.items.map((item) => ({ id: item.id, author: item.author, text: item.text, parentId: item.parent_id, time: formatFeedDate(item.created_at) }));
  }

  async addAdComment(adId: string, text: string, parentId?: string): Promise<AdComment> {
    const body = await request<{ comment: { id: string; author: string; text: string; parent_id?: string | null; created_at: string } }>(
      `/api/v1/ads/${encodeURIComponent(adId)}/comments/create`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, parent_id: parentId }) },
    );
    const item = body.comment;
    return { id: item.id, author: item.author, text: item.text, parentId: item.parent_id, time: formatFeedDate(item.created_at) };
  }

  async getConversations(): Promise<Conversation[]> {
    const body = await request<ConversationCollectionResponse>("/api/v1/chats/conversations");
    const mapped = body.items.map(mapConversation);
    return [...mapped.filter((item) => !item.isSupport), ...mapped.filter((item) => item.isSupport)];
  }

  async getInbox(): Promise<ChatInbox> {
    const body = await request<InboxResponse>("/api/v1/chats/inbox");
    return { items: body.items.map(mapInboxItem), unreadCount: body.unread_count };
  }

  async setTelegramWriteAccess(granted: boolean): Promise<boolean> {
    const body = await request<{ granted: boolean }>("/api/v1/chats/inbox/telegram-write-access", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ granted }),
    });
    return body.granted === true;
  }

  async blockConversation(chatId: string): Promise<boolean> {
    const body = await request<{ blocked: boolean }>(
      `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/block`,
      { method: "POST" },
    );
    return body.blocked === true;
  }

  async unblockConversation(chatId: string): Promise<boolean> {
    const body = await request<{ blocked: boolean }>(
      `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/block`,
      { method: "DELETE" },
    );
    return body.blocked === true;
  }

  async markConversationSold(chatId: string): Promise<Conversation> {
    const body = await request<ConversationResponse>(
      `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/sold`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
    );
    return mapConversation(body.conversation);
  }

  async rateAd(adId: string, conversationId: string, rating: number, text = ""): Promise<void> {
    await request<unknown>(`/api/v1/ads/${encodeURIComponent(adId)}/rate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversation_id: conversationId, rating, text }),
    });
  }

  async reportMessage(
    chatId: string,
    messageId: string,
    reason: "spam" | "fraud" | "abuse" | "other",
  ): Promise<void> {
    await request<unknown>(
      `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/report`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      },
    );
  }

  async startConversation(listingId: string): Promise<Conversation> {
    const body = await request<ConversationResponse>("/api/v1/chats/conversations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ad_id: listingId }),
    });
    return mapConversation(body.conversation);
  }

  async getConversationMessages(chatId: string, afterSequence?: number): Promise<ChatMessage[]> {
    const afterQuery = typeof afterSequence === "number" ? `?after_seq=${afterSequence}` : "";
    const body = await request<MessageCollectionResponse>(
      isSupportChat(chatId)
        ? `/api/v1/chats/support/messages${afterQuery}`
        : `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/messages${afterQuery}`,
    );
    return Promise.all(body.items.map((message) => this.materializeMessage(chatId, message)));
  }

  async markConversationRead(chatId: string, lastReadSeq: number): Promise<number> {
    const body = await request<{ last_read_seq: number }>(
      isSupportChat(chatId)
        ? "/api/v1/chats/support/read"
        : `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/read`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ last_read_seq: lastReadSeq }),
      },
    );
    return body.last_read_seq;
  }

  subscribeToConversation(
    chatId: string,
    listener: ConversationEventListener,
    onReconnect?: (afterSequence: number) => void,
    initialSequence = 0,
  ): () => void {
    if (isSupportChat(chatId)) {
      let closed = false;
      let pollingTimer: number | null = null;
      const poll = () => {
        if (closed || document.visibilityState === "hidden") return;
        onReconnect?.(initialSequence);
        pollingTimer = window.setTimeout(poll, 10_000);
      };
      pollingTimer = window.setTimeout(poll, 10_000);
      return () => {
        closed = true;
        if (pollingTimer !== null) window.clearTimeout(pollingTimer);
      };
    }
    let closed = false;
    let latestSequence = initialSequence;
    let socket: WebSocket | null = null;
    let retry = 0;
    let reconnectTimer: number | null = null;
    let pollingTimer: number | null = null;
    const reconnectDelays = [1_000, 2_000, 4_000, 8_000, 15_000];
    const isForeground = () => typeof document === "undefined" || document.visibilityState !== "hidden";
    const clearReconnect = () => {
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };
    const stopPolling = () => {
      if (pollingTimer !== null) window.clearTimeout(pollingTimer);
      pollingTimer = null;
    };
    const schedulePolling = () => {
      if (closed || !isForeground() || pollingTimer !== null) return;
      pollingTimer = window.setTimeout(() => {
        pollingTimer = null;
        if (closed || !isForeground()) return;
        onReconnect?.(latestSequence);
        schedulePolling();
      }, 10_000);
    };
    const connect = () => {
      if (closed || !isForeground()) return;
      if (socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) return;
      const scheme = window.location.protocol === "https:" ? "wss" : "ws";
      try {
        socket = new WebSocket(`${scheme}://${window.location.host}/ws/v1/chats/${encodeURIComponent(chatId)}`);
      } catch {
        scheduleReconnect();
        return;
      }
      socket.onopen = () => {
        retry = 0;
        clearReconnect();
        stopPolling();
        onReconnect?.(latestSequence);
      };
      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(String(event.data)) as {
            event_id?: string;
            type?: string;
            entity_id?: string;
            version?: number;
            server_time?: string;
            message?: ServerMessage;
            read?: { author?: "me" | "other"; last_read_seq?: number };
          };
          const hasEnvelope =
            typeof parsed.event_id === "string" &&
            typeof parsed.entity_id === "string" &&
            typeof parsed.version === "number" &&
            typeof parsed.server_time === "string";
          if (!hasEnvelope) return;
          if (parsed.type === "message.created" && parsed.message) {
            void this.materializeMessage(chatId, parsed.message)
              .then((message) => {
                latestSequence = Math.max(latestSequence, message.sequence ?? 0);
                listener({ type: "message.created", message });
              })
              .catch(() => undefined);
            return;
          }
          if (
            parsed.type === "conversation.read" &&
            (parsed.read?.author === "me" || parsed.read?.author === "other") &&
            typeof parsed.read.last_read_seq === "number"
          ) {
            listener({
              type: "conversation.read",
              author: parsed.read.author,
              lastReadSequence: parsed.read.last_read_seq,
            });
          }
        } catch {
          // A malformed server event is ignored; HTTP history remains the source of truth.
        }
      };
      socket.onerror = () => socket?.close();
      socket.onclose = () => {
        socket = null;
        scheduleReconnect();
      };
    };
    const scheduleReconnect = () => {
      if (closed || !isForeground() || reconnectTimer !== null) return;
      schedulePolling();
      const delay = reconnectDelays[Math.min(retry, reconnectDelays.length - 1)];
      retry = Math.min(retry + 1, reconnectDelays.length - 1);
      const jitter = Math.floor(Math.random() * 251);
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay + jitter);
    };
    const visibilityHandler = () => {
      if (closed) return;
      if (isForeground()) {
        retry = 0;
        onReconnect?.(latestSequence);
        connect();
        return;
      }
      clearReconnect();
      stopPolling();
      socket?.close();
    };
    if (typeof WebSocket === "undefined") {
      schedulePolling();
      return () => {
        closed = true;
        stopPolling();
      };
    }
    document.addEventListener("visibilitychange", visibilityHandler);
    connect();
    return () => {
      closed = true;
      clearReconnect();
      stopPolling();
      document.removeEventListener("visibilitychange", visibilityHandler);
      socket?.close();
    };
  }

  async sendMessage(
    chatId: string,
    text: string,
    files: File[] = [],
    clientMessageId = newClientMessageId(),
  ): Promise<ChatMessage> {
    if (isSupportChat(chatId)) {
      if (files.length) throw new Error("attachment_invalid");
      if (!text.trim()) throw new Error("message_invalid");
      const body = await request<MessageResponse>("/api/v1/chats/support/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text, client_message_id: clientMessageId }),
      });
      return this.materializeMessage(chatId, body.message);
    }
    const assetIds = await this.uploadChatAttachments(files, clientMessageId);
    if (!text.trim() && !assetIds.length) throw new Error("message_invalid");
    const body = await request<MessageResponse>(
      `/api/v1/chats/conversations/${encodeURIComponent(chatId)}/messages`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          body: text,
          client_message_id: clientMessageId,
          media_asset_ids: assetIds,
        }),
      },
    );
    return this.materializeMessage(chatId, body.message);
  }

  getDraft(): Draft {
    return { ...this.draft };
  }

  async saveDraft(patch: Partial<Draft>): Promise<Draft> {
    this.draft = { ...this.draft, ...patch };
    return this.getDraft();
  }

  async submitDraft(draft: Draft, files: File[]): Promise<Listing> {
    if (!files.length || files.length > 12) throw new Error("media_required");
    const created = await request<CreatedAdResponse>("/api/v1/ads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(listingPayload(draft)),
    });
    const adId = created.ad.id;
    for (let position = 0; position < files.length; position += 1) {
      const file = files[position];
      const assetId = await this.commitUpload(file);
      await request<unknown>(`/api/v1/ads/${encodeURIComponent(adId)}/media`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ asset_id: assetId, position }),
      });
    }
    const published = await request<PublishedAdResponse>(
      `/api/v1/ads/${encodeURIComponent(adId)}/publish`,
      { method: "POST" },
    );
    this.draft = emptyDraft();
    this.submittedState = true;
    return mapAd(published.ad);
  }
}

export function isServerUser(user: AuthenticatedTelegramUser | null): boolean {
  return Boolean(user?.telegram_id);
}
